import { useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Interactive } from '@highlight/ui'
import { useChatImage } from '@renderer/chat/hooks/useChatImage'
import { useSidepanelRenderedWidth, useSidepanelSide } from '@renderer/overlay/Sidepanel/atoms'

export function useImageSource(props: { src?: string; fileId?: string }) {
  const { imageUrl, isImageLoading, imageError } = useChatImage(props.fileId ?? '')
  if (props.src) return { src: props.src, loading: false, error: false }
  return { src: imageUrl ?? undefined, loading: isImageLoading, error: !!imageError }
}

interface ImageViewProps {
  src: string
  alt?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Plain div + Interactive instead of Dialog to avoid focus management
// conflicts with the overlay's click-through system.
export function ImageView({ src, alt = 'Attachment', open, onOpenChange }: ImageViewProps) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange])
  const side = useSidepanelSide()
  const sidepanelWidth = useSidepanelRenderedWidth()

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    const onSidepanelClick = (e: MouseEvent) => {
      const sidepanel = document.querySelector('[data-sidepanel-root]')
      if (sidepanel && sidepanel.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onSidepanelClick)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onSidepanelClick)
    }
  }, [open, close])

  if (!open) return null

  return createPortal(
    <Interactive>
      <div className="fixed inset-0 z-[29] bg-black/80" onClick={close} />
      {/* Reserve the sidepanel's strip on whichever edge it's docked to. */}
      <div
        className="pointer-events-none fixed inset-0 z-[200] flex items-center justify-center p-8"
        style={{ [side]: sidepanelWidth }}
      >
        <div className="pointer-events-auto relative max-h-[80vh] max-w-[720px]" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={close}
            className="bg-background text-foreground absolute right-2 top-2 z-20 flex size-6 cursor-pointer items-center justify-center rounded-full shadow-sm outline-none"
            aria-label="Close"
          >
            <X className="size-3" />
          </button>
          <img src={src} alt={alt} className="max-h-full max-w-full rounded-lg object-contain" draggable={false} />
        </div>
      </div>
    </Interactive>,
    document.body,
  )
}
